import { makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { FETCH_DEADHEADS } from 'gql/deadheadSearch';
import { FETCH_RIDES_WITH_FILTERS } from 'gql/rideSearch';
import { TIME_ZONES_ADDITIONS } from 'utils/datetime';

import {
	DEADHEAD_ID,
	DEADHEAD_SEARCH,
	DEFAULT_PAGE_NUMBER,
	ITEMS_PER_PAGE,
	PROBLEMATIC_ID,
	RIDE_SEARCH,
	TABS_MODS,
} from './const';

class RideListStore {
	isRidesLoading = false;
	isRidesError = false;
	isDeadheadsTabActive = false;
	isShowingFallbackRides = false;
	pageCount = DEFAULT_PAGE_NUMBER;
	activePage = DEFAULT_PAGE_NUMBER;
	searchMode = TABS_MODS.get(PROBLEMATIC_ID).tabId;
	selectedTimezone = TIME_ZONES_ADDITIONS.stationTime;
	rides = [];
	lastFilters = {};
	latestRequestId = 0;

	constructor() {
		makeAutoObservable(this);
	}

	setSearchMode = (mode) => {
		this.isRidesLoading = true;
		this.searchMode = mode;
		this.isDeadheadsTabActive = mode === DEADHEAD_ID;
	};

	setSelectedTimezone = (timezone) => {
		this.selectedTimezone = timezone;
	};

	setActivePage = (page) => {
		this.activePage = typeof page === 'function' ? page(this.activePage) : page;
	};

	get tabsMods() {
		return TABS_MODS;
	}

	fetchRides = async (filters, isRideIdApplied, forceRidesApi = false) => {
		this.isRidesLoading = true;

		try {
			const actualFilters = { ...filters };

			// Use the filter store to check for rideId
			if (!isRideIdApplied && !this.isDeadheadsTabActive) {
				actualFilters.mode = this.searchMode;
			}

			// Check if filters changed
			const filtersChanged = JSON.stringify(this.lastFilters) !== JSON.stringify(actualFilters);
			const pageToUse = filtersChanged ? DEFAULT_PAGE_NUMBER : this.activePage;

			// Determine which API to use
			const useRidesApi = forceRidesApi || !this.isDeadheadsTabActive;
			const queryToUse = useRidesApi ? FETCH_RIDES_WITH_FILTERS : FETCH_DEADHEADS;

			const requestId = ++this.latestRequestId;
			const { data } = await client.query({
				query: queryToUse,
				variables: {
					pagination: {
						page: pageToUse,
						size: ITEMS_PER_PAGE,
					},
					filter: { ...actualFilters },
				},
				fetchPolicy: 'no-cache',
			});

			// Use the correct dataKey based on which API was actually called
			const dataKey = useRidesApi ? RIDE_SEARCH : DEADHEAD_SEARCH;

			// Ignore obsolete responses
			if (requestId !== this.latestRequestId) {
				return;
			}

			runInAction(() => {
				this.rides = data?.[dataKey]?.results;
				this.pageCount = data?.[dataKey]?.page || DEFAULT_PAGE_NUMBER;
				this.activePage = data?.[dataKey]?.page.page || DEFAULT_PAGE_NUMBER;
				this.lastFilters = { ...actualFilters };
				this.isRidesLoading = false;

				// Reset fallback flag when doing regular searches
				if (!isRideIdApplied) {
					this.isShowingFallbackRides = false;
				}
			});
		} catch (error) {
			runInAction(() => {
				this.isRidesLoading = false;
				this.isRidesError = true;
				this.rides = [];
				this.isShowingFallbackRides = false;
			});
		}
	};

	resetFallbackState = () => {
		this.isShowingFallbackRides = false;
	};

	// TODO: CLARIFY DEADHEAD SEARCH
	// fetchDeadheadByIdWithFallback = async (deadheadId) => {
	// 	this.isRidesLoading = true;
	//
	// 	try {
	// 		// Try deadhead API first
	// 		const deadheadResult = await this.tryDeadheadApi(deadheadId);
	// 		if (deadheadResult) {
	// 			this.setDeadheadResult(deadheadResult, deadheadId);
	// 			return;
	// 		}
	//
	// 		// Fallback to rides API
	// 		await this.tryRidesFallback(deadheadId);
	// 	} catch (error) {
	// 		console.log('Error in fetchDeadheadByIdWithFallback:', error);
	// 		await this.handleFallbackError(deadheadId);
	// 	}
	// };

	// tryDeadheadApi = async (deadheadId) => {
	// 	const { data } = await client.query({
	// 		query: FETCH_DEADHEAD_BY_ID,
	// 		variables: { deadheadId },
	// 		fetchPolicy: 'no-cache',
	// 	});
	//
	// 	if (data?.getDeadheadById && data.getDeadheadById !== null) {
	// 		return data.getDeadheadById;
	// 	}
	//
	// 	return null;
	// };

	// tryRidesFallback = async (deadheadId) => {
	// 	const { data } = await client.query({
	// 		query: FETCH_RIDES_WITH_FILTERS,
	// 		variables: {
	// 			pagination: {
	// 				page: DEFAULT_PAGE_NUMBER,
	// 				size: ITEMS_PER_PAGE,
	// 			},
	// 			filter: { rideId: deadheadId },
	// 		},
	// 		fetchPolicy: 'no-cache',
	// 	});
	//
	// 	this.setRidesResult(data, deadheadId);
	// };

	// handleFallbackError = async (deadheadId) => {
	// 	try {
	// 		console.log('Deadhead API failed, trying rides API fallback');
	// 		await this.tryRidesFallback(deadheadId);
	// 	} catch (fallbackError) {
	// 		this.setErrorState();
	// 	}
	// };

	// setDeadheadResult = (deadhead, deadheadId) => {
	// 	runInAction(() => {
	// 		this.rides = [deadhead];
	// 		// Set pagination for 1 result
	// 		this.pageCount = {
	// 			page: 0,
	// 			size: 10,
	// 			totalPages: 1,
	// 			totalCount: 1,
	// 			__typename: 'PageInfo',
	// 		};
	// 		this.activePage = 0;
	// 		this.lastFilters = { rideId: deadheadId };
	// 		this.isRidesLoading = false;
	// 		this.isShowingFallbackRides = false;
	// 	});
	// };

	// setRidesResult = (data, deadheadId) => {
	// 	runInAction(() => {
	// 		this.rides = data?.rideSearch?.results || [];
	// 		this.pageCount = data?.rideSearch?.page || DEFAULT_PAGE_NUMBER;
	// 		this.activePage = data?.rideSearch?.page?.page || DEFAULT_PAGE_NUMBER;
	// 		this.lastFilters = { rideId: deadheadId };
	// 		this.isRidesLoading = false;
	// 		this.isShowingFallbackRides = true;
	// 	});
	// };

	// setErrorState = () => {
	// 	runInAction(() => {
	// 		this.isRidesLoading = false;
	// 		this.isRidesError = true;
	// 		this.rides = [];
	// 		this.isShowingFallbackRides = false;
	// 	});
	// };
}

export const rideListStore = new RideListStore();
